<script setup lang="ts">
import SearchBarField from '@/components/Globals/SearchBarField.vue'
import MultiSelect from '@/components/Forms/MultiSelect.vue'
import IcAnimateSpin from '@/components/Icons/IcAnimateSpin.vue'
import IcSearch from '@/components/Icons/IcSearch.vue'
import DailyTemperaturePeriodFields from '@/components/DailyTemperature/DailyTemperaturePeriodFields.vue'
import type { IDailyTemperaturePeriodFilter } from '@/interfaces/entities/daily-temperature'
import type { ISensor } from '@/interfaces/entities/sensor'
import type { IShift } from '@/interfaces/entities/shifts'
import { FIELD_INPUT_CLASS } from '@/utils/daily-temperature/period-filter'
import { useI18n } from '@/composable/useI18n'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { computed, ref } from 'vue'

const props = defineProps<{
  primaryDate: string
  compareDate: string
  isCompareEnabled: boolean
  periodFilter: IDailyTemperaturePeriodFilter
  sensorIds: number[]
  temperatureSensors: ISensor[]
  shifts: IShift[]
  hasShifts: boolean
  maxDate: string
  isLoading: boolean
}>()

const emit = defineEmits<{
  'update:primaryDate': [value: string]
  'update:compareDate': [value: string]
  'update:isCompareEnabled': [value: boolean]
  'update:periodFilter': [value: IDailyTemperaturePeriodFilter]
  'update:sensorIds': [value: number[]]
  'clear-filters': []
  search: []
}>()

const { t } = useI18n()

const multiSelectRef = ref<{ clearSelection: () => void } | null>(null)

const sensorOptions = computed(() => {
  const selectedIds = new Set(props.sensorIds)
  return props.temperatureSensors.map((sensor) => ({
    id: sensor.id,
    value: sensor.id,
    text: sensor.name,
    selected: selectedIds.has(sensor.id),
  }))
})

function enableComparison() {
  emit('update:isCompareEnabled', true)
}

function disableComparison() {
  emit('update:compareDate', '')
  emit('update:isCompareEnabled', false)
}

function onSensorsSelected(values: Array<string | number>) {
  emit(
    'update:sensorIds',
    values.map((value) => Number(value)).filter((id) => Number.isFinite(id)),
  )
}

function handleClearFilters() {
  multiSelectRef.value?.clearSelection()
  emit('clear-filters')
}
</script>

<template>
  <div class="flex w-full min-w-0 flex-col p-4 [&_input]:rounded-none [&_button]:rounded-none [&_select]:rounded-none">
    <div class="flex w-full min-w-0 flex-col items-stretch gap-3 lg:flex-row lg:items-end lg:flex-nowrap">
      <SearchBarField :label="t('daily_temperature.fields.primary_date')" class="min-w-0 flex-1 basis-0">
        <input
          :value="primaryDate"
          type="date"
          :max="maxDate"
          :class="FIELD_INPUT_CLASS"
          @input="emit('update:primaryDate', ($event.target as HTMLInputElement).value)" />
      </SearchBarField>

      <SearchBarField
        v-if="isCompareEnabled"
        :label="t('daily_temperature.fields.compare_date')"
        class="min-w-0 flex-1 basis-0">
        <div class="relative min-w-0">
          <input
            :value="compareDate"
            type="date"
            :max="maxDate"
            :class="`${FIELD_INPUT_CLASS} w-full pr-9`"
            @input="emit('update:compareDate', ($event.target as HTMLInputElement).value)" />
          <button
            type="button"
            class="absolute right-0 top-0 flex h-full w-8 items-center justify-center border-l border-zinc-300 text-gray-500 hover:bg-gray-2 hover:text-red"
            :aria-label="t('daily_temperature.buttons.remove_comparison')"
            @click="disableComparison">
            <XMarkIcon class="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </SearchBarField>

      <div v-else class="min-w-0 flex-1 basis-0">
        <span
          class="invisible inline-flex self-start px-2 py-1 text-xs font-medium"
          aria-hidden="true">
          {{ t('daily_temperature.buttons.add_comparison') }}
        </span>
        <button
          type="button"
          class="w-full rounded-none border border-stroke bg-white px-3 py-3 text-sm font-medium text-gray-700 hover:bg-gray-2"
          @click="enableComparison">
          {{ t('daily_temperature.buttons.add_comparison') }}
        </button>
      </div>

      <DailyTemperaturePeriodFields
        compact
        :period-filter="periodFilter"
        :shifts="shifts"
        :has-shifts="hasShifts"
        @update:period-filter="emit('update:periodFilter', $event)" />
    </div>

    <div class="mt-6 flex w-full min-w-0 flex-col items-stretch gap-3 lg:flex-row lg:items-end lg:flex-nowrap">
      <SearchBarField :label="t('daily_temperature.fields.sensors')" class="min-w-0 flex-1">
        <MultiSelect
          ref="multiSelectRef"
          :options="sensorOptions"
          @update:selected-values="onSensorsSelected" />
      </SearchBarField>

      <div class="flex w-full shrink-0 flex-col gap-2 sm:flex-row sm:items-end lg:w-auto">
        <button
          type="button"
          class="flex w-full flex-row items-center justify-center rounded-none border border-stroke bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-2 lg:min-w-[140px]"
          @click="handleClearFilters">
          {{ t('daily_temperature.buttons.clear_filters') }}
        </button>
        <button
          type="button"
          :disabled="isLoading"
          class="flex w-full flex-row items-center justify-center gap-2 rounded-none border border-transparent bg-blue px-4 py-3 text-sm font-semibold text-white hover:bg-blue-2 lg:min-w-[120px]"
          @click="emit('search')">
          <IcSearch v-if="!isLoading" class="h-5 w-5 shrink-0" />
          <span v-else class="animate-spin">
            <IcAnimateSpin class="h-5 w-5 text-white" />
          </span>
          {{ t('daily_temperature.buttons.search') }}
        </button>
      </div>
    </div>
  </div>
</template>
